import { DataTableAlignmentColumnDef } from './features/ColumnAlignment.js';
import type { DataTableConfig } from './public.api.js';

type RowDensity = 'default' | 'condensed' | 'comfortable';

// DataTable config props
export const DEFAULT_DATATABLE_CONFIG: DataTableConfig = {
  fullWidth: false,
  fullHeight: false,
  lineWrap: false,
  selectableRows: undefined,
  resizable: false,
  sortable: false,
  pagination: {},
  variant: {
    contained: true,
    fontStyle: 'text',
    headers: 'default',
    rowDensity: 'default',
    rowSeparation: 'horizontalDividers',
    verticalDividers: false,
  },
  columnVisibility: {},
  rowThresholds: [],
};

// Visual variant defaults
export const DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT = {
  rowSeparation: 'horizontalDividers',
  verticalDividers: false,
  contained: true,
  rowDensity: 'default',
  fontStyle: 'text',
  headers: 'default',
  verticalAlignment: 'top',
} as const;

// Pagination
// ----------
/** Default page index if pagination is defined, but no page index is set. */
export const DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX = 0;
/** Default page size options for pagination. */
export const DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS = [
  10, 20, 50, 100, 250, 500, 1000,
] as const;
/** Default page size option (100). */
export const DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION =
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS[3];

// Table sizing
// -------------------------
/** Minimum allowed width of a DataTable column (px). */
export const DATA_TABLE_MIN_WIDTH = 120;
/** Minimum width for empty state (px). */
export const DATA_TABLE_EMPTY_MIN_WIDTH = 200;
/** Border width for DataTable (px). */
export const DATA_TABLE_BORDER_WIDTH = 2;
/** Border width for DataTable when visual refresh is enabled (px). */
export const DATA_TABLE_BORDER_WIDTH_VISUAL_REFRESH = 1;

// Column sizing and resizing
// -------------------------
/** Default minimum width of a DataTable column (px). */
export const DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH = 30;
/** Default width of a DataTable column (px). */
export const DATA_TABLE_DEFAULT_COLUMN_WIDTH = 150;
/** Width of the column resize handle (px). */
export const DATA_TABLE_COLUMN_RESIZE_HANDLE_WIDTH = 3;
/** Minimum width for built-in columns (px). */
export const DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH = 24;
/** Spacer width for sub-rows (px). */
export const DATA_TABLE_SUB_ROW_SPACER = 16;
/** Indentation per sub-row level (px). */
export const DATA_TABLE_SUB_ROW_INDENT_GRID = DATA_TABLE_SUB_ROW_SPACER;
/** Pill size for threshold indicators (px). */
export const DATA_TABLE_THRESHOLD_PILL_SIZE = 4;
/** Width of the cell and column action button (px). */
export const DATA_TABLE_ACTION_BUTTON_WIDTH = 24;
/**
 * When hovering over a header cell, extra CSS padding is applied to make room for the action menu trigger.
 * However, if the column is too narrow, this extra padding causes layout issues.
 * To prevent this, we enforce a minimum width threshold:
 * if the column width is below this threshold, no extra padding is added.
 */
export const DATA_TABLE_NARROW_HEADER_THRESHOLDS: Record<RowDensity, number> = {
  condensed: 28 + 6,
  default: 34 + 12,
  comfortable: 38 + 16,
};
export const DATA_TABLE_HEADER_SORTED_OFFSET_MARGIN = -22; // 20px icon + 2px spacing
export const DATA_TABLE_HEADER_MULTISORT_OFFSET_MARGIN = -30; // 28px bubble + 2px spacing
export const DATA_TABLE_HEADER_ACTION_OFFSET_MARGIN = -22;
export const DATA_TABLE_HEADER_SORTED_ACTION_OFFSET_MARGIN =
  DATA_TABLE_HEADER_SORTED_OFFSET_MARGIN +
  DATA_TABLE_HEADER_ACTION_OFFSET_MARGIN;
export const DATA_TABLE_HEADER_MULTISORT_ACTION_OFFSET_MARGIN =
  DATA_TABLE_HEADER_MULTISORT_OFFSET_MARGIN +
  DATA_TABLE_HEADER_ACTION_OFFSET_MARGIN;

// Row sizing and padding
// ---------------------
/** Default row heights for different densities (px). */
export const DATA_TABLE_ROW_HEIGHT_MAP: Record<RowDensity, number> = {
  condensed: 24,
  default: 36,
  comfortable: 44,
};
/** Default top/bottom padding values for different row densities (px). */
export const DATA_TABLE_CELL_VERTICAL_PADDING_MAP: Record<RowDensity, number> =
  {
    condensed: 2,
    default: 8,
    comfortable: 12,
  };
/** Paddings for the sub-row indicator column (px). */
export const DATA_TABLE_SUB_ROW_DENSITY_PADDING_MAP =
  DATA_TABLE_CELL_VERTICAL_PADDING_MAP;
/** Default left/right padding values for different row densities (px). */
export const DATA_TABLE_CELL_HORIZONTAL_PADDING_MAP: Record<
  RowDensity,
  number
> = {
  condensed: 6,
  default: 12,
  comfortable: 16,
};
/** Border width for rows when row separation is set to 'horizontalDividers' (px). */
export const DATA_TABLE_ROW_BORDER = 1;
/** Height of horizontal divider (px). */
export const DATA_TABLE_HORIZONTAL_DIVIDER_HEIGHT = 1;
/** Default min row height (px). */
export const DATA_TABLE_DEFAULT_MIN_ROW_HEIGHT =
  DATA_TABLE_ROW_HEIGHT_MAP.condensed;

/** Width of the horizontal scroll shadow (px). */
export const DATA_TABLE_HORIZONTAL_SCROLL_SHADOW_WIDTH = 50;

// Z-index and layering
// --------------------
/** Z-index values used for DataTable layering. */
export const DATA_TABLE_ZINDEX_CONFIG = {
  DEFAULT: 1,
  STICKY_LEFT: 2,
  STICKY_RIGHT: 3, // must be higher than left pinned columns to ensure deterministic overlap
  PINNED_RESIZE_HANDLE: 3,
  OVERLAY: 4,
  HEADER: 5,
  STICKY_SCROLL_SHADOW: 6,
} as const;

// Built-in column IDs
// -------------------
export const DATA_TABLE_ROW_DRAG_COLUMN_ID = 'DataTableRowDragColumn';
export const DATA_TABLE_ROW_SELECTION_COLUMN_ID = 'DataTableRowSelectionColumn';
export const DATA_TABLE_ROW_DETAILS_COLUMN_ID = 'DataTableRowDetailsColumn';
export const DATA_TABLE_ROW_ACTIONS_COLUMN_ID = 'DataTableRowActions';
export const DATA_TABLE_SPACER_COLUMN_ID = 'DataTableSpacerColumn';
export const DATA_TABLE_BUILT_IN_COLUMN_IDS = new Set([
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
  DATA_TABLE_ROW_SELECTION_COLUMN_ID,
  DATA_TABLE_ROW_DETAILS_COLUMN_ID,
  DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
  DATA_TABLE_SPACER_COLUMN_ID,
]);

// Row identifiers and suffixes
// ---------------------------
/** Symbol used to identify a row as a details row. */
export const DATA_TABLE_DETAILS_ROW_IDENTIFIER = Symbol(
  'DETAILS_ROW_IDENTIFIER',
);
/** Appended to row id of cloned expanded row. */
export const DATA_TABLE_ROW_EXPANDED_ID_SUFFIX = '-expanded';
/** Appended to row id of a pinning shadow cell. */
export const DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX = '-shadow-cell';
/** Symbol used to identify a rows' parity. */
export const DATA_TABLE_DETAILS_ROW_PARITY = Symbol('DETAILS_ROW_PARITY');
/** Symbol used to indicate that an action applies to all columns. */
export const DATA_TABLE_ACTION_FOR_ALL_COLUMNS = Symbol(
  'Action applies to all columns',
);

// CSV/Download
// ------------
/** Default name of the downloaded CSV file. */
export const DATA_TABLE_DOWNLOAD_CSV_FILE_NAME = 'table-data.csv';
/** Column header prepended to CSV file if table has sub-rows. */
export const DATA_TABLE_INDEX_COLUMN_HEADER = 'index';

// CSS variables
// ---------------------------------
/** CSS variable for left position during column resizing. */
export const DATA_TABLE_RESIZING_LEFT_POSITION_CSS_VAR =
  '--resizing-leftPosition';
/** CSS variable for right position during column resizing. */
export const DATA_TABLE_RESIZING_RIGHT_POSITION_CSS_VAR =
  '--resizing-rightPosition';
/** CSS variable for resizing size. */
export const DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR = '--strato-resizing-size';
/** CSS variable for drag-and-drop placeholder divider top offset. */
export const DATA_TABLE_DND_PLACEHOLDER_DIVIDER_CSS_VAR =
  '--column-dnd-placeholder-divider-top';

// CSS styles
/** Opacity value for non-target elements during drag-and-drop. */
export const DATA_TABLE_DND_NON_TARGET_OPACITY = 0.2;

// Virtualization
// --------------
/** Overscan row count for virtualizer. */
export const DATA_TABLE_VIRTUALIZER_ROW_OVERSCAN = 20;
/** Overscan column count for virtualizer. */
export const DATA_TABLE_VIRTUALIZER_COLUMN_OVERSCAN = 5;
/** Default scrollTo options for virtualizer. */
export const DATA_TABLE_DEFAULT_SCROLL_OPTIONS = {
  align: 'start',
  behavior: 'auto',
} as const;

// Miscellaneous
// -------------
/** Default icon size (px). */
export const DATA_TABLE_DEFAULT_ICON_SIZE = 20;
/** Default icon padding (px). */
export const DATA_TABLE_DEFAULT_ICON_PADDING = 2;
/** Icon size for sub-row expander (px). */
export const DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE =
  DATA_TABLE_DEFAULT_ICON_SIZE;
/** Default horizontal alignment for columns if not specified. */
export const DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT: Required<DataTableAlignmentColumnDef>['alignment'] =
  'left';
